import React, { useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { toast } from 'react-toastify';
import moment from 'moment';

import { Formik, Form } from 'formik';
import { Grid, Typography, Button as MUIButton, FormControlLabel, Checkbox, TextField, Chip } from '@material-ui/core';
import { Autocomplete, createFilterOptions } from '@material-ui/lab';

import Modal from 'components/Modal';
import Loader from 'components/Loader';
import Card from 'components/Card';
import Button from 'components/Button';
import Input from 'components/Input';
import Select from 'components/Select';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCopy } from '@fortawesome/free-solid-svg-icons';

import { loadBusinessUuid } from '../../OpenAPI/store/thunk';
import { loadToken, upsertToken, renewToken, removeToken } from '../store/thunk';
import { clearErrors } from '../store/actions';
import { loadRestrictions } from 'pages/EmployeesRegistration/store/thunk';
import { loadFilters } from 'components/Filter/store/thunk';

import { expirationDaysOptions, noAllowedDomainsExpirationDaysOptions } from './resources';
import { urlRegExp, ipv6RegExp } from '../../../helpers/regex';

import { validationSchema } from './validations';

export default function OpenAPIRegistration() {
  const dispatch = useDispatch();

  const history = useHistory();

  const { userIntegrationId, expirationDate } = history.location.state?.data || {};

  const isCreate = !userIntegrationId;
  const isTokenExpired = moment(expirationDate).utc(true).isBefore(moment.now());

  const { userId } = useSelector(state => state.profile);
  const { establishments } = useSelector(state => state.businessInfo);
  const { businessUuid } = useSelector(state => state.openApi);
  const { name, expirationDays, allowedDomains, establishmentIds, restrictions, isLoading, errors: responseErrors } = useSelector(state => state.openApiRegistration);
  const { restrictionsUser } = useSelector(state => state.employeesRegistration);
  const { selects } = useSelector(state => state.filters);

  const [isRenewTokenModalOpen, setIsRenewTokenModalOpen] = useState(false);
  const [renewExpirationDays, setRenewExpirationDays] = useState('');

  const [isRemoveTokenModalOpen, setIsRemoveTokenModalOpen] = useState(false);
  const [isRemoveTokenConfirmed, setIsRemoveTokenConfirmed] = useState(false);

  const [isRegenerateApiKeyModalOpen, setIsRegenerateApiKeyModalOpen] = useState(false);

  const [isSuccessModalOpen, setIsSuccessModalOpen] = useState(false);
  const [successToken, setSuccessToken] = useState('');
  const [successIntegrationId, setSuccessIntegrationId] = useState('');
  const [isSuccessConfirmed, setIsSuccessConfirmed] = useState(false);

  const [allowedDomain, setAllowedDomain] = useState('');
  const allowedDomainInputRef = useRef(null);

  const formattedEstablishments = (establishments || [])
    ?.filter(establishment => +establishment?.premiumPackage == 1)
    ?.map(establishment => ({
      value: establishment?.establishmentId,
      label: establishment?.establishmentName
    }));

  const restrictionsTypes = selects?.find(filter => filter?.id == 'restrictionType')?.options || [];
  const restrictionsToDisplay = [103, 104, 105, 106, 117, 118];

  const groupedRestrictionsUser = restrictionsTypes.map(restrictionType => ({
    ...restrictionType,
    restrictions: (restrictionsUser || []).filter(restriction => restriction?.type == restrictionType?.id)
  }));

  useEffect(() => {
    dispatch(clearErrors());
  }, []);

  useEffect(() => {
    if(!!userIntegrationId) {
      dispatch(loadToken(userId, businessUuid, userIntegrationId));
    }
  }, [userIntegrationId]);

  useEffect(() => {
    if(userId) {
      dispatch(loadBusinessUuid(userId));
      dispatch(loadRestrictions(userId));
      dispatch(loadFilters(userId));
    }
  }, [userId]);

  const filterOptions = (options, state) => {
    const OPTIONS_LIMIT = 500;
    const defaultFilterOptions = createFilterOptions();

    return defaultFilterOptions(options, state).slice(0, OPTIONS_LIMIT);
  }

  const handleSubmit = values => {
    const params = {
      ...values,
      restrictions: values?.restrictions?.length > 0 ? values?.restrictions : []
    }

    if(!isCreate) {
      delete params['expirationDays'];
    }

    dispatch(upsertToken(userId, businessUuid, userIntegrationId, params))
      .then(data => {
        if(isCreate) {
          setSuccessToken(data?.token?.publicApiToken || '');
          setSuccessIntegrationId(data?.user?.userId || '');
          setIsSuccessModalOpen(true);
          setIsSuccessConfirmed(false);

          return;
        }

        history.push('/api-aberta');
      });
  }

  const handleConfirmRenewToken = () => {
    const params = {
      expirationDays: renewExpirationDays || 0
    }

    dispatch(renewToken(userId, businessUuid, userIntegrationId, params))
      .then(data => {
        setSuccessToken(data?.token?.publicApiToken || '');
        setSuccessIntegrationId(data?.user?.userId || userIntegrationId);
        setIsSuccessModalOpen(true);
        setIsSuccessConfirmed(false);
        setIsRenewTokenModalOpen(false);
        setRenewExpirationDays('');
      })
  }

  const handleConfirmRemoveToken = () => {
    dispatch(removeToken(userId, businessUuid, userIntegrationId))
      .then(() => history.push('/api-aberta'));
  }

  const getInitialValues = () => {
    if(!!userIntegrationId) {
      return {
        name,
        expirationDays,
        allowedDomains,
        establishmentIds,
        restrictions
      }
    }

    return {
      name: '',
      expirationDays: '',
      allowedDomains: [],
      establishmentIds: [],
      restrictions: []
    }
  }

  return(
    <Grid container spacing={2}>
      <Grid item xs={12}>
        {isRenewTokenModalOpen && (
          <Modal
            id="renewTokenModal"
            title="Confirmação de renovação"
            scroll="body"
            maxWidth="sm"
            open={isRenewTokenModalOpen}
            onClose={() => {
              setIsRenewTokenModalOpen(false);
              setRenewExpirationDays('');
            }}
            modalActions={
              <>
                <Button
                  className="mr-3"
                  color="error"
                  variant="outlined"
                  loading={isLoading}
                  onClick={() => {
                    setIsRenewTokenModalOpen(false);
                    setRenewExpirationDays('');
                  }}
                >
                  Cancelar
                </Button>
                <Button color="success" loading={isLoading} onClick={handleConfirmRenewToken}>
                  Renovar
                </Button>
              </>
            }
          >
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Typography>
                  Deseja renovar o token?
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <Select
                  name="expirationDays"
                  label="Expiração"
                  options={expirationDaysOptions}
                  value={renewExpirationDays}
                  onChange={event => setRenewExpirationDays(event.target.value)}
                />
              </Grid>
            </Grid>
          </Modal>
        )}
        {isRegenerateApiKeyModalOpen && (
          <Modal
            id="regenerateApiKeyModal"
            title="Confirmação"
            scroll="body"
            maxWidth="sm"
            open={isRegenerateApiKeyModalOpen}
            onClose={() => setIsRegenerateApiKeyModalOpen(false)}
            modalActions={
              <>
                <Button
                  className="mr-3"
                  color="error"
                  variant="outlined"
                  loading={isLoading}
                  onClick={() => setIsRegenerateApiKeyModalOpen(false)}
                >
                  Cancelar
                </Button>
                <Button
                  color="success"
                  loading={isLoading}
                  onClick={() => null}
                >
                  Regenerar
                </Button>
              </>
            }
          >
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Typography>
                  Você deseja regenerar a chave de API?
                </Typography>
              </Grid>
            </Grid>
          </Modal>
        )}
        {isRemoveTokenModalOpen && (
          <Modal
            id="removeTokenModal"
            title="Atenção"
            scroll="body"
            maxWidth="sm"
            open={isRemoveTokenModalOpen}
            onClose={() => {
              setIsRemoveTokenModalOpen(false);
              setIsRemoveTokenConfirmed(false);
            }}
            modalActions={
              <>
                <Button
                  className="mr-3"
                  color="error"
                  variant="outlined"
                  loading={isLoading}
                  onClick={() => {
                    setIsRemoveTokenModalOpen(false);
                    setIsRemoveTokenConfirmed(false);
                  }}
                >
                  Cancelar
                </Button>
                <Button
                  color="error"
                  loading={isLoading}
                  disabled={!isRemoveTokenConfirmed}
                  onClick={handleConfirmRemoveToken}
                >
                  Remover Token
                </Button>
              </>
            }
          >
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Typography>
                  Ao remover esse token, todas as funções das APIs com este token serão interrompidas.
                  <br />
                  <br />
                  Certifique que deseja interromper a integração ou já realizou a troca de token.
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={isRemoveTokenConfirmed}
                      onChange={event => setIsRemoveTokenConfirmed(event.target.checked)}
                    />
                  }
                  label={
                    <Typography>
                      Estou ciente das informações acima
                    </Typography>
                  }
                />
              </Grid>
            </Grid>
          </Modal>
        )}
        {isSuccessModalOpen && (
          <Modal
            id="successTokenModal"
            title="Salve seu token e ID de integração"
            scroll="body"
            maxWidth="sm"
            open={isSuccessModalOpen}
            onClose={() => setIsSuccessModalOpen(false)}
            modalActions={
              <Button
                color="success"
                loading={isLoading}
                disabled={!isSuccessConfirmed}
                onClick={() => {
                  setIsSuccessModalOpen(false);
                  setIsSuccessConfirmed(false);
                  history.push('/api-aberta');
                }}
              >
                Feito
              </Button>
            }
          >
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Typography>
                  Guarde seu token e ID de integração em local seguro. Depois, ele não poderá ser visualizado novamente.
                  Se você o perder, será necessário gerar um novo.
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography>
                  Este método é para deixar a sua integração ainda mais segura.
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography>
                  Token
                </Typography>
                <Grid container alignItems='center' spacing={1}>
                  <Grid item xs={9}>
                    <Input
                      disabled
                      name="successToken"
                      value={successToken}
                    />
                  </Grid>
                  <Grid item xs={3}>
                    <MUIButton
                      type='button'
                      color='primary'
                      onClick={() => {
                        if(successToken) {
                          navigator.clipboard.writeText(successToken);
                          toast.success('Token copiado!');
                        }
                      }}
                      startIcon={
                        <FontAwesomeIcon
                          icon={faCopy}
                          color="#022A5C"
                          size="xs"
                        />
                      }
                    >
                      Copiar
                    </MUIButton>
                  </Grid>
                </Grid>
              </Grid>
              <Grid item xs={12}>
                <Typography>
                  ID de integração
                </Typography>
                <Grid container alignItems='center' spacing={1}>
                  <Grid item xs={9}>
                    <Input
                      disabled
                      name="successIntegrationId"
                      value={successIntegrationId}
                    />
                  </Grid>
                  <Grid item xs={3}>
                    <MUIButton
                      type='button'
                      color='primary'
                      onClick={() => {
                        if(successIntegrationId) {
                          navigator.clipboard.writeText(successIntegrationId);
                          toast.success('ID de integração copiado!');
                        }
                      }}
                      startIcon={
                        <FontAwesomeIcon
                          icon={faCopy}
                          color="#022A5C"
                          size="xs"
                        />
                      }
                    >
                      Copiar
                    </MUIButton>
                  </Grid>
                </Grid>
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={isSuccessConfirmed}
                      onChange={event => setIsSuccessConfirmed(event.target.checked)}
                    />
                  }
                  label={
                    <Typography>
                      Salvei meu token e ID e estou ciente que não verei novamente.
                    </Typography>
                  }
                />
              </Grid>
            </Grid>
          </Modal>
        )}
        <Formik
          enableReinitialize
          initialValues={getInitialValues()}
          validationSchema={validationSchema}
          validateOnChange={false}
          onSubmit={handleSubmit}
        >
          {({ values, errors, handleChange, setFieldValue, setFieldError }) => (
            <Card title="Informações básicas">
              <Form style={{ width: '100vw' }}>
                <Loader isLoading={isLoading}>
                  <Grid container spacing={1}>
                    <Grid item xs={12} md={6}>
                      <Input
                        type="text"
                        name="name"
                        label="Nome"
                        value={values.name}
                        error={!!errors.name}
                        helperText={errors.name}
                        onChange={handleChange}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      {isCreate ? (
                        <Select
                          name="expirationDays"
                          label="Expiração"
                          options={values.allowedDomains.length > 0 ? expirationDaysOptions : noAllowedDomainsExpirationDaysOptions}
                          value={values.expirationDays}
                          error={!!errors.expirationDays || !!responseErrors?.expirationDays}
                          helperText={errors.expirationDays || responseErrors?.expirationDays}
                          onChange={handleChange}
                        />
                      ) : (
                        <Input
                          disabled
                          type="text"
                          name="expirationDays"
                          label="Expiração"
                          value={(values.expirationDays == 0 || values.expirationDays == null || values.expirationDays == '0001-01-01 00:00:00') ? 'Sem expiração' : `${values.expirationDays} dia(s)`}
                        />
                      )}
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        size="small"
                        color="primary"
                        variant="outlined"
                        margin="normal"
                        name="allowedDomains"
                        label="IPs ou domínios de origem"
                        value={allowedDomain}
                        error={errors.allowedDomains}
                        helperText={errors.allowedDomains}
                        inputRef={allowedDomainInputRef}
                        onChange={event => setAllowedDomain(event.target.value)}
                        onKeyDown={event => {
                          if(event.key == 'Enter') {
                            event.preventDefault();
                          }
                        }}
                        onKeyUp={event => {
                          const domain = event?.target?.value?.trim();

                          if(event.key == 'Enter' || (event.key == ' ' && domain != '')) {
                            setFieldError('allowedDomains', '');

                            if(!urlRegExp.test(domain) && !ipv6RegExp.test(domain)) {
                              setFieldError('allowedDomains', 'O IP ou domínio deve ser válido');
                              return;
                            }

                            if(values?.allowedDomains?.includes(domain)) {
                              setAllowedDomain('');
                              return;
                            }

                            if(!!values.allowedDomains) {
                              setFieldValue('allowedDomains', [...values.allowedDomains, domain]);
                              setAllowedDomain('');
                            }
                          }
                        }}
                        InputProps={{
                          startAdornment: (!!values.allowedDomains && values.allowedDomains.length > 0) && (
                            <div
                              style={{ marginTop: 25 }}
                              onClick={() => allowedDomainInputRef.current?.focus()}
                              role="presentation"
                            >
                              {values.allowedDomains.map(origin => {
                                return(
                                  <Chip
                                    style={{ margin: 5 }}
                                    variant="outlined"
                                    key={origin}
                                    label={origin}
                                    onClick={() => allowedDomainInputRef.current?.focus()}
                                    onDelete={() => setFieldValue('allowedDomains', [...values.allowedDomains.filter(item => item != origin)])}
                                  />
                                );
                              })}
                            </div>
                          ),
                          style: (values.allowedDomains.length == 0)
                            ? {}
                            : {
                              display: 'grid',
                              gridTemplateColumns: '1fr',
                              gridTemplateRows: '1fr 35px'
                            }
                        }}
                      />
                      <Typography variant='body2' color='textSecondary'>
                        Escreva um IP ou domínio de origem no campo acima e aperte 'Enter' ou 'Espaço' para persistir.
                      </Typography>
                      <Typography variant='body2' color='textSecondary'>
                        Caso faça acesso da sua rede doméstica use o site <a href='https://whatismyipaddress.com/' target='_blank'>What Is My IP Address</a> para pegar seu IPv4 e IPv6.
                      </Typography>
                      {(values.allowedDomains == 0) && (
                        <Typography variant='body2' color='error'>
                          Atenção: IPs ou domínios de origem não preenchido.
                        </Typography>
                      )}
                    </Grid>
                    <Grid item xs={12} style={{ backgroundColor: '#D9D9D9', borderRadius: 8, padding: 10, marginTop: 10, marginBottom: 10 }}>
                      <Typography>
                        <b>
                          Você pode personalizar a integração para que terceiros se integrem mas não consigam acessar funções específicas.
                        </b>
                      </Typography>
                    </Grid>
                    <Grid item xs={12}>
                      <Typography color='primary' variant='h6'>
                        Quais estabelecimentos a integração pode acessar?
                      </Typography>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Autocomplete
                        multiple
                        disableCloseOnSelect
                        id="establishmentIds"
                        options={formattedEstablishments}
                        filterOptions={filterOptions}
                        value={formattedEstablishments?.filter(establishment => values?.establishmentIds?.includes(establishment.value)) || []}
                        onChange={(_, establishments) => {
                          return setFieldValue('establishmentIds', establishments?.map(establishment => establishment?.value));
                        }}
                        getOptionLabel={option => option.label}
                        getOptionSelected={(option, value) => option.value == value.value}
                        renderInput={params => (
                          <TextField
                            {...params}
                            variant="outlined"
                            color="primary"
                            label="Estabelecimentos"
                            error={!!errors.establishmentIds}
                            helperText={errors.establishmentIds}
                          />
                        )}
                        renderOption={(option, { selected }) => (
                          <li color="primary" style={{ display: 'flex', alignItems: 'center' }}>
                            <Checkbox checked={selected} />
                            <Typography color="primary">{option.label}</Typography>
                          </li>
                        )}
                        renderTags={(options, getTagProps) => {
                          return options.map((option, index) => (
                            <Chip
                              {...getTagProps({ index })}
                              label={option.label}
                            />
                          ))
                        }}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <Typography color='primary' variant='h6'>
                        Crie restrições de funções da API
                      </Typography>
                    </Grid>
                    <Grid item xs={12}>
                      {(groupedRestrictionsUser || [])?.filter(group => (group?.restrictions?.filter(restriction => restrictionsToDisplay.includes(+restriction?.restrictionId)) || []).length > 0).map(group => (
                        <Grid item xs={12} key={group?.label || group?.name}>
                          <Typography color='textSecondary' variant='h6'>
                            {group?.label}
                          </Typography>
                          <Grid container spacing={1}>
                            {(group?.restrictions || [])?.filter(restriction => restrictionsToDisplay.includes(+restriction?.restrictionId))?.map(restriction => (
                              <Grid item xs={12} md={6} key={restriction?.name}>
                                <FormControlLabel
                                  control={<Checkbox checked={values?.restrictions?.includes(restriction?.restrictionId)} />}
                                  label={
                                    <Typography color='primary'>
                                      {restriction?.name}
                                    </Typography>
                                  }
                                  onChange={event => {
                                    if(event.target.checked) {
                                      return setFieldValue('restrictions', [...values.restrictions, restriction?.restrictionId]);
                                    }

                                    if(!event.target.checked) {
                                      return setFieldValue('restrictions', values.restrictions?.filter(restrictionId => restrictionId != restriction?.restrictionId));
                                    }
                                  }}
                                />
                              </Grid>
                            ))}
                          </Grid>
                        </Grid>
                      ))}
                      <Grid container direction='row' justify='flex-end' style={{ marginTop: 20, gap: 10 }}>
                        {(!isCreate && isTokenExpired) && (
                          <Button
                            type='button'
                            color='primary'
                            onClick={() => setIsRenewTokenModalOpen(true)}
                          >
                            Renovar Token
                          </Button>
                        )}
                        {!isCreate && (
                          <Button
                            type='button'
                            color='error'
                            onClick={() => setIsRemoveTokenModalOpen(true)}
                          >
                            Remover Token
                          </Button>
                        )}
                        <Button
                          type='submit'
                          color='primary'
                        >
                          Salvar
                        </Button>
                      </Grid>
                    </Grid>
                  </Grid>
                </Loader>
              </Form>
            </Card>
          )}
        </Formik>
      </Grid>
    </Grid>
  );
}